// src/store/reducers/virtualSwapReducer.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { base_url } from '../../utils/config'

// 1️⃣ Stage an item
export const addSwapItem = createAsyncThunk(
  'swap/add',
  async (payload, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.post(
        `${base_url}/api/virtual-swap/add`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return res.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

// 2️⃣ Fetch staged items
export const fetchSwapItems = createAsyncThunk(
  'swap/fetchAll',
  async (_, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.get(
        `${base_url}/api/virtual-swap/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return res.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

// 2.5️⃣ Get count only
export const getSwapCount = createAsyncThunk(
  'swap/getCount',
  async (_, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.get(
        `${base_url}/api/virtual-swap/count`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return res.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

// 3️⃣ Remove one staged pick
export const removeSwapItem = createAsyncThunk(
  'swap/remove',
  async (itemId, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.delete(
        `${base_url}/api/virtual-swap/item/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return res.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

// 4️⃣ Generate try‑on images
export const generateSwap = createAsyncThunk(
  'swap/generate',
  async ({ userImage, picks }, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.post(
        `${base_url}/api/virtual-swap/generate`,
        { userImage, picks },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return res.data.generated
    } catch (e) {
      return rejectWithValue(e.response?.data || { error: 'Generation failed' })
    }
  }
)

// 5️⃣ Fetch saved assets
export const fetchAssets = createAsyncThunk(
  'swap/fetchAssets',
  async (_, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.get(
        `${base_url}/api/virtual-swap/assets`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return res.data.items
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

// 6️⃣ Remove one saved asset
export const removeAsset = createAsyncThunk(
  'swap/removeAsset',
  async (assetId, { rejectWithValue, getState }) => {
    const { token } = getState().auth
    try {
      const res = await axios.delete(
        `${base_url}/api/virtual-swap/assets/${assetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // assume backend returns { id: assetId }
      return res.data.id
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

const initialState = {
  items:                 [],   // staged picks
  generated:             [],   // last generation batch
  assets:                [],   // saved assets
  virtual_swap_count:    0,    // count of staged items
  loading:               false,
  error:                 null,
  successMessage:        '',
  errorMessage:          ''
}

const slice = createSlice({
  name: 'virtualSwap',
  initialState,
  reducers: {
    messageClear: (state) => {
      state.errorMessage = ''
      state.successMessage = ''
    },
    reset_swap_count: (state) => {
      state.virtual_swap_count = 0
    }
  },
  extraReducers: builder =>
    builder
      // addSwapItem
      .addCase(addSwapItem.pending,   state => { state.loading = true })
      .addCase(addSwapItem.fulfilled, (state, { payload }) => {
        state.loading = false
        state.successMessage = payload.message
        state.items.push(payload.item)
        state.virtual_swap_count = payload.virtual_swap_count
      })
      .addCase(addSwapItem.rejected,  (state, { payload }) => {
        state.loading = false
        state.errorMessage = payload.error || 'Add failed'
      })

      // fetchSwapItems
      .addCase(fetchSwapItems.pending,   state => { state.loading = true })
      .addCase(fetchSwapItems.fulfilled, (state, { payload }) => {
        state.loading = false
        state.items = payload.items
        state.virtual_swap_count = payload.virtual_swap_count
      })
      .addCase(fetchSwapItems.rejected,  (state, { payload }) => {
        state.loading = false
        state.errorMessage = payload.error || 'Fetch failed'
      })

      // getSwapCount
      .addCase(getSwapCount.fulfilled, (state, { payload }) => {
        state.virtual_swap_count = payload.virtual_swap_count
      })

      // removeSwapItem
      .addCase(removeSwapItem.pending,   state => { state.loading = true })
      .addCase(removeSwapItem.fulfilled, (state, { payload }) => {
        state.loading = false
        state.successMessage = payload.message
        state.items = state.items.filter(i => i._id !== payload.id)
        state.virtual_swap_count = payload.virtual_swap_count
      })
      .addCase(removeSwapItem.rejected,  (state, { payload }) => {
        state.loading = false
        state.errorMessage = payload.error || 'Remove failed'
      })

      // generateSwap
      .addCase(generateSwap.pending,   state => { state.loading = true })
      .addCase(generateSwap.fulfilled, (state, { payload }) => {
        state.loading   = false
        state.generated = payload
      })
      .addCase(generateSwap.rejected,  (state, { payload }) => {
        state.loading = false
        state.errorMessage = payload.error || 'Swap generation failed'
      })

      // fetchAssets
      .addCase(fetchAssets.pending,   state => { state.loading = true })
      .addCase(fetchAssets.fulfilled, (state, { payload }) => {
        state.loading = false
        state.assets  = payload
      })
      .addCase(fetchAssets.rejected,  (state, { payload }) => {
        state.loading = false
        state.errorMessage = payload.error || 'Assets fetch failed'
      })

      // removeAsset
      .addCase(removeAsset.pending,   state => { state.loading = true })
      .addCase(removeAsset.fulfilled, (state, { payload }) => {
        state.loading = false
        state.assets = state.assets.filter(a => a._id !== payload)
      })
      .addCase(removeAsset.rejected,  (state, { payload }) => {
        state.loading = false
        state.errorMessage = payload.error || 'Asset removal failed'
      })
})

export const { messageClear, reset_swap_count } = slice.actions
export default slice.reducer
